import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import _pick from 'lodash/pick';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import type { ANNOTATION_SHAPE_STYLING_TYPE } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import type { MousePosT, StagePointerEventT } from '../../../providers/stage-provider';
import { type RectangleAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import Shape from '../shape/shape';
import { VERTEX_TYPE } from '../shape/vertex';
import { getStylesForStylingType } from '../utils';
import useRectangle from './hooks/useRectangle';
import { constrainRectangleToStageBounds, getRectangleVertices, getTransformedPointsOnVertexMove, getTranslatedRectangle } from './utils';
type PropsT = {
  isDrawing?: boolean;
  annotation?: RectangleAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
  stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE];
};
const Rectangle = ({
  isDrawing,
  annotation,
  annotationIdx,
  disableChanges,
  stylingType
}: PropsT) => {
  const {
    stage,
    addAnnotation,
    updateAnnotation,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom,
    focusedAnnotationIdAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const initialRect = useRef<RectangleAnnotationT | null>(null);
  const initialMousePos = useRef<MousePosT | null>(null);
  const onRectangleCreation = useCallback((rect, options) => {
    addAnnotation(rect, {
      ..._pick(options, ['actionDuration'])
    });
  }, [addAnnotation]);
  const {
    rect,
    labelCoords,
    centerCoords,
    setRotationAngle,
    rotationAngle,
    setRect
  } = useRectangle(!!isDrawing, {
    onRectangleCreation,
    initialState: annotation
  });
  const {
    start,
    end
  } = useActionDuration();
  const [selectedVertexType, setSelectedVertexType] = useState<(typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE] | undefined | null>(null);
  const rectangleRef = useRef<SVGRectElement>(null);
  const isLocked = Boolean(annotation?.locked);
  const isEditable = editableAnnotation === annotationIdx;
  const isFocused = focusedAnnotationId === annotationIdx;
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (annotation && selectedVertexType !== VERTEX_TYPE.ROTATE) {
      if (!selectedVertexType && stageEvent.detail.dragTarget !== rectangleRef.current) {
        return;
      }
      if (!initialRect.current || !initialMousePos.current) {
        initialRect.current = annotation;
        initialMousePos.current = stageEvent.detail.mousePos;
      }
      const newRect = selectedVertexType ? getTransformedPointsOnVertexMove(annotation, stageEvent.detail.mousePos, selectedVertexType, null, initialRect.current, initialMousePos.current) : getTranslatedRectangle(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation);

      // Check if rectangle would be out of bounds of the stage
      const [stageWidth, stageHeight] = getStageDimension();
      const {
        rect: constrainedRect
      } = constrainRectangleToStageBounds(newRect, stageWidth, stageHeight);
      setRect(constrainedRect);
      start();
    } else if (selectedVertexType === VERTEX_TYPE.ROTATE) {
      setRotationAngle({
        mouseCoords: {
          x: stageEvent.detail.mousePos[0],
          y: stageEvent.detail.mousePos[1]
        },
        center: centerCoords ? centerCoords : [0, 0]
      });
      if (annotation && rect) {
        let newRect = {
          ...annotation,
          data: {
            ...rect.data,
            rotationAngle: rotationAngle
          }
        };
        setRect(newRect);
      }
    }
  }, [annotation, selectedVertexType, start, setRotationAngle, centerCoords, rect, rotationAngle, setRect, getStageDimension]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation || annotationIdx === undefined) {
      return;
    }
    // For move drag, if the target is not same ignore the event.
    if (selectedVertexType !== VERTEX_TYPE.ROTATE) {
      if (!selectedVertexType && stageEvent.detail.dragTarget !== rectangleRef.current) {
        return;
      }
      const newRect = selectedVertexType ? getTransformedPointsOnVertexMove(annotation, stageEvent.detail.mousePos, selectedVertexType, null, initialRect.current, initialMousePos.current) : getTranslatedRectangle(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation);
      initialRect.current = null;
      initialMousePos.current = null;

      // Check if rectangle would be out of bounds of the stage
      const [stageWidth, stageHeight] = getStageDimension();
      const {
        rect: finalRect
      } = constrainRectangleToStageBounds(newRect, stageWidth, stageHeight);
      setRect(finalRect);
      updateAnnotation(annotationIdx, {
        ...finalRect
      }, {
        actionDuration: end()
      });
      setSelectedVertexType(null);
    }
    if (selectedVertexType === VERTEX_TYPE.ROTATE) {
      if (annotationIdx && rect) {
        // No bounds check for rotated rectangles
        updateAnnotation(annotationIdx, {
          ...rect,
          data: {
            ...rect.data,
            rotationAngle: rotationAngle
          }
        }, {
          actionDuration: end()
        });
      }
      setSelectedVertexType(null);
    }
  }, [annotation, annotationIdx, selectedVertexType, updateAnnotation, end, rect, rotationAngle, setRect, getStageDimension]);
  useEffect(() => {
    if (!stage || disableChanges || isLocked || !isLabelingMode(editorMode)) return;
    if (isEditable) {
      stage.addEventListener('drag', onDrag);
      stage.addEventListener('dragend', onDragEnd);
      return () => {
        stage.removeEventListener('drag', onDrag);
        stage.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isEditable, stage, onDrag, onDragEnd, disableChanges, isLocked, editorMode]);
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  if (!rect) return null;
  const vertices = getRectangleVertices(rect.data);
  const annotationStyle = getAnnotationStyle(rect.annotationLabel.style, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    isM4: isVideoLabelingM4Enabled
  });
  return <Shape vertices={vertices} isLocked={isLocked} isDrawing={isDrawing} annotationLabel={{
    ...rect.annotationLabel,
    labelCoords
  }} annotationIdx={annotationIdx} metadataPillText={dynamicAttributesText} onVertexSelect={({
    type
  }) => setSelectedVertexType(type)} rotationAngle={rect.data ? rect.data.rotationAngle : 0} rotationAllowed={true} centerCoords={centerCoords} dataAttributes={{
    'data-e2e-id': `annotation-${rect.annotationLabel.title}-${rect.annotationType}`,
    'data-annotation-id': annotationIdx?.toString() || ''
  }}>
      <rect x={rect.data.x} y={rect.data.y} height={rect.data.height} width={rect.data.width} stroke={annotationStyle.strokeColor} fill={annotationStyle.fillColor} fillOpacity={annotationStyle.fillOpacity} style={{
      strokeWidth: annotationStyle.strokeWidth
    }} ref={rectangleRef}
    // convert radians to degrees
    transform={`rotate(${(rect.data.rotationAngle || 0) * 180 / Math.PI} ${centerCoords[0]} ${centerCoords[1]})`} {...getStylesForStylingType(stylingType)} />
    </Shape>;
};
export default Rectangle;